import { Box, Typography, Grid, Chip } from '@mui/material';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';
import { getCountryImageByNationality } from '@/helpers/images-helper';
import { getCountryNameInSpanish } from '@/utils/country';
import CustomImageComponent from '../shared/custom/custom-image';
import { toPriceInEuros } from '@/helpers/prices-helper';
import { FANTASY_MODE } from '@/helpers/biwenger-helper';

const NoticiaFlashJugadorInfo = ({ jugadorPlantilla }) => {
    if (!jugadorPlantilla) return null;

    const getModeLabel = (modeNumber) => {
        const modeLabels = {
            [FANTASY_MODE.TITULARIDAD]: 'Titularidad',
            [FANTASY_MODE.DIARIO_AS]: 'Diario AS',
            [FANTASY_MODE.SOFASCORE]: 'Sofascore',
            [FANTASY_MODE.STATS]: 'Stats',
            [FANTASY_MODE.AVERAGE]: 'Average',
            [FANTASY_MODE.SOCIAL]: 'Social',
            [FANTASY_MODE.CARRUSEL_DEPORTIVO]: 'Carrusel Deportivo',
        };
        return modeLabels[modeNumber] || `Modo ${modeNumber}`;
    };

    const InfoRow = ({ label, value }) => {
        if (!value && value !== 0) return null;
        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{label}:</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{value}</Typography>
            </Box>
        );
    };

    const FantasyBlock = ({ title, data, color = 'accent' }) => {
        if (!data) return null;

        const getSubidaColor = (subida) => {
            if (!subida) return 'text.primary';
            return subida > 0 ? 'success.main' : 'error.main';
        };

        const shouldShowModeLabel = data.modos && data.modos.length > 1;

        return (
            <CustomSectionWrapper title={title} color={color} mt={2}>
                <Grid container spacing={1}>
                    {/* Valor de mercado y subida */}
                    <Grid size={12}>
                        {data.marketValue && (
                            <InfoRow label="Valor de mercado" value={toPriceInEuros(data.marketValue)} />
                        )}
                        {data.subida && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                    Subida:
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        color: getSubidaColor(data.subida),
                                    }}
                                >
                                    {data.subida > 0 ? '+' : ''}
                                    {toPriceInEuros(data.subida)}
                                </Typography>
                            </Box>
                        )}
                    </Grid>

                    {/* Modos */}
                    {data.modos?.map((modo, index) => {
                        if (!modo.total) return null;

                        return (
                            <Grid size={12} key={index}>
                                {shouldShowModeLabel && (
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 1, mb: 0.5 }}>
                                        {getModeLabel(modo.mode)}
                                    </Typography>
                                )}
                                <InfoRow label="Puntos" value={modo.total?.points} />
                                <InfoRow label="Partidos" value={modo.total?.played} />
                                <InfoRow
                                    label="Media"
                                    value={
                                        modo.total?.points && modo.total?.played
                                            ? (modo.total.points / modo.total.played).toFixed(2)
                                            : null
                                    }
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            </CustomSectionWrapper>
        );
    };

    const estadisticas = jugadorPlantilla.estadisticas;

    return (
        <Box>
            {/* Información General */}
            <CustomSectionWrapper title="Información General" mt={2}>
                {jugadorPlantilla.nationality && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CustomImageComponent
                            src={getCountryImageByNationality(jugadorPlantilla.nationality, 24)}
                            alt={jugadorPlantilla.nationality}
                            width={24}
                            height={16}
                            style={{ borderRadius: '2px' }}
                        />
                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                            {getCountryNameInSpanish(jugadorPlantilla.nationality)}
                        </Typography>
                    </Box>
                )}

                <InfoRow label="Lugar de nacimiento" value={jugadorPlantilla.birthPlace} />
                <InfoRow label="Fecha de nacimiento" value={jugadorPlantilla.birthDate} />

                {jugadorPlantilla.tier && (
                    <Box sx={{ mt: 1 }}>
                        <Chip
                            label={`Tier: ${jugadorPlantilla.tier}`}
                            size="small"
                            sx={{
                                bgcolor: 'background.darkCard',
                                fontWeight: 600,
                            }}
                        />
                    </Box>
                )}
            </CustomSectionWrapper>

            {/* Estadísticas Generales */}
            {estadisticas && (
                <CustomSectionWrapper title="Estadísticas Temporada" mt={2}>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <InfoRow label="Goles" value={estadisticas.goles} />
                            <InfoRow label="Asistencias" value={estadisticas.asistencias} />
                            <InfoRow label="Titularidades" value={estadisticas.titularidades} />
                        </Grid>
                        <Grid size={6}>
                            <InfoRow label="Amarillas" value={estadisticas.amarillas} />
                            <InfoRow label="Rojas" value={estadisticas.rojas} />
                            <InfoRow label="Minutos" value={estadisticas.minutos} />
                        </Grid>
                    </Grid>
                </CustomSectionWrapper>
            )}

            {/* Bloques de Fantasy */}
            <FantasyBlock title="La Liga Fantasy" data={jugadorPlantilla.laLigaFantasy} color="accent" />
            <FantasyBlock title="Fantasy Marca" data={jugadorPlantilla.fantasyMarca} color="accent2" />
            <FantasyBlock title="Mister Fantasy" data={jugadorPlantilla.misterFantasy} color="accent3" />
            <FantasyBlock title="Biwenger" data={jugadorPlantilla.biwenger} color="played" />
        </Box>
    );
};

export default NoticiaFlashJugadorInfo;
